/**
 * UpcomingPaymentsTab Component
 * Displays upcoming payments with date filter controls
 */

import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { format } from 'date-fns';
import { formatNumber } from '../../utils/validation';
import ContractReferenceCell from '../contracts/ContractReferenceCell';
import SortableHeaderCell from '../common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';

const UPCOMING_COLUMNS = [
  { key: 'contract',      align: 'left',  getValue: (p) => p.contract_reference || p.reference },
  { key: 'investor',      align: 'left',  getValue: (p) => p.investor_name },
  { key: 'due_date',      align: 'left',  getValue: (p) => p.due_date || p.payment_date },
  { key: 'type',          align: 'left',  getValue: (p) => p.payment_type },
  { key: 'amount',        align: 'right', getValue: (p) => p.expected_amount != null ? parseFloat(p.expected_amount) : (p.amount != null ? parseFloat(p.amount) : null) },
  { key: 'status',        align: 'left',  getValue: (p) => p.status || p.payment_status },
];

function UpcomingPaymentsTab({
  payments = [],
  currencyCode = 'ZAR',
  showContractColumn = true,
  showInvestorColumn = false,
  showMatchActions = true,
  onMatch,
  filter = '30days',
  onFilterChange,
  customDate,
  onCustomDateChange,
  investorNameField = 'investor_name',
  showFilter = true,
}) {
  const sort = useTableSort();
  const sortedPayments = sort.sortRows(payments, UPCOMING_COLUMNS);
  return (
    <Box>
      {/* Filter controls */}
      {showFilter && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Show</InputLabel>
            <Select
              value={filter}
              onChange={(e) => onFilterChange && onFilterChange(e.target.value)}
              label="Show"
            >
              <MenuItem value="30days">Next 30 Days</MenuItem>
              <MenuItem value="90days">Next 90 Days</MenuItem>
              <MenuItem value="all">All Future</MenuItem>
              <MenuItem value="custom">Custom Date</MenuItem>
            </Select>
          </FormControl>

          {filter === 'custom' && (
            <TextField
              type="date"
              size="small"
              value={customDate ? format(customDate, 'yyyy-MM-dd') : ''}
              onChange={(e) => onCustomDateChange && onCustomDateChange(e.target.value ? new Date(e.target.value) : null)}
              InputLabelProps={{ shrink: true }}
              label="Show until"
              sx={{ minWidth: 180 }}
            />
          )}

          <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
            {payments.length} payment{payments.length !== 1 ? 's' : ''} in selected period
          </Typography>
        </Box>
      )}

      {payments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="textSecondary" fontWeight="medium" gutterBottom>
            No upcoming payments in selected period
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try selecting a longer time period to see more payments
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {showContractColumn && <SortableHeaderCell sort={sort} columnKey="contract">Contract</SortableHeaderCell>}
                {showInvestorColumn && <SortableHeaderCell sort={sort} columnKey="investor">Investor</SortableHeaderCell>}
                <SortableHeaderCell sort={sort} columnKey="due_date">Due Date</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="type">Type</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="amount" align="right">Amount</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="status">Status</SortableHeaderCell>
                {showMatchActions && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPayments.map((payment, index) => (
                <TableRow
                  key={payment.id || `upcoming-${index}`}
                  hover
                  onClick={showMatchActions && onMatch ? () => onMatch(payment) : undefined}
                  sx={showMatchActions && onMatch ? { cursor: 'pointer' } : undefined}
                >
                  {showContractColumn && (
                    <TableCell>
                      <ContractReferenceCell reference={payment.contract_reference || '-'} userReference={payment.user_reference} />
                    </TableCell>
                  )}
                  {showInvestorColumn && (
                    <TableCell>
                      <Typography variant="body2">
                        {payment[investorNameField] || '-'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    {payment.payment_date
                      ? format(new Date(payment.payment_date), 'dd MMM yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.narration || 'Payment'}
                      size="small"
                      variant="outlined"
                      color={
                        payment.narration?.includes('Interest') ? 'info' :
                        payment.narration?.includes('Capital') ? 'success' : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="medium">
                      {payment.currency_code || currencyCode} {formatNumber(payment.payment_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label="Pending" size="small" variant="outlined" />
                  </TableCell>
                  {showMatchActions && (
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {onMatch && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => onMatch(payment)}
                        >
                          Match
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default UpcomingPaymentsTab;
